import React from 'react'
import { PaletteProvider } from '../../../utils/palette'
import merge from 'deepmerge'

import {
  FIRST_MODULE_ID,
  toSiblingMap,
  __example__devModuleIds as devModuleIds, // eslint-disable-line
} from '../../../utils/sibling-spacing'

import { EVEntry } from '.'
import ModMarker, { displayModMarker } from './module-marker'
import { ConditionalWrapper } from '../../../utils/conditional-wrapper'

import { applyToPairWhen, sequentialGroupBy } from '../../../utils/arr'
import { contentTypeId } from '../../../utils/contentful'

import { usePalette, useMeta, useIncludes, getEntity } from '../selectors'
import { constructModIds, constructSiblingSpacingLookupMap, extractModIdParts } from './page'

const PaletteWrapper = ({ palette, children }) => (
  <PaletteProvider value={usePalette(palette)}>{children}</PaletteProvider>
)

const isCriticalImage = ({ selectEntityObj, withinBounds, content, includes }) => {
  const entity = getEntity(selectEntityObj(content), includes)
  return !entity.critical &&
    withinBounds &&
    (contentTypeId(entity) === 'imageModule' || contentTypeId(entity) === 'imageCalloutModule')
    ? true
    : entity.critical
}

export const Modular = ({ modules = [], ...rest }) => {
  const includes = useIncludes()
  const siblingSpacing = useMeta(['siblingSpacing'], [])
  const imageCriticalBound = useMeta(['imageCriticalBound'])
  const siblingSpacingMap = toSiblingMap(siblingSpacing)
  const restoreTopAndBottomSpacing = useMeta(['restoreTopAndBottomSpacing'], true)

  modules = modules.map((mod, i) => ({ ...mod, originalIndex: i }))
  const extractedDevModules = modules.filter(
    (mod) => devModuleIds.indexOf(contentTypeId(getEntity(mod, includes))) > -1,
  )

  const [spacedSansDevModules] = applyToPairWhen(
    modules.filter((mod) => devModuleIds.indexOf(contentTypeId(getEntity(mod, includes))) === -1),
    (prevMod, currMod, i) => {
      const a = i !== 0 ? extractModIdParts(getEntity(prevMod, includes)) : undefined
      const b = extractModIdParts(getEntity(currMod, includes))

      const A = i !== 0 ? constructModIds(a) : [FIRST_MODULE_ID]
      const B = constructModIds(b)

      const lookupMap = constructSiblingSpacingLookupMap(A, B)

      return siblingSpacingMap[lookupMap.find((i) => siblingSpacingMap[i])]
    },
    (index, mod, spacingConfig) =>
      merge(mod, {
        spacing: spacingConfig.spacing[index] || {},
      }),
  )
  const moduleListLength = spacedSansDevModules.length

  // Need to re-inject the dev modules back into the list of spaced modules
  // We loop over each extracted dev module and splice it back into the space modules
  const spacedModules = extractedDevModules.reduce((acc, { originalIndex, ...mod }) => {
    const pre = acc.slice(0, originalIndex)
    const post = acc.slice(originalIndex)

    return [...pre, mod, ...post]
  }, spacedSansDevModules)

  const groupedModules = sequentialGroupBy(
    spacedModules,
    (mod) => contentTypeId(getEntity(mod, includes)) === 'palette',
  )

  return (
    <>
      {displayModMarker() && <ModMarker entity={rest} />}
      {groupedModules
        .filter(([, g]) => g.length)
        .map(([palette, modules], key) => {
          /** Use palette wrapper if a palette exists, otherwise just children */
          return (
            <ConditionalWrapper
              key={key}
              condition={!!palette}
              wrapper={PaletteWrapper}
              palette={palette}
            >
              {modules.map((entity, idx) => {
                const index = entity?.originalIndex
                const isCritical = isCriticalImage({
                  selectEntityObj: (x) => x,
                  withinBounds:
                    imageCriticalBound && Number(imageCriticalBound)
                      ? index <= imageCriticalBound
                      : undefined,
                  content: entity,
                  includes,
                })
                return (
                  <EVEntry
                    {...entity}
                    key={index}
                    parentContentType={contentTypeId(rest)}
                    track={true}
                    moduleIndex={`modularpage-${index}`}
                    critical={isCritical}
                    removeTopSpacing={!restoreTopAndBottomSpacing ? key === 0 && idx === 0 : false}
                    removeBottomSpacing={
                      !restoreTopAndBottomSpacing ? index + 1 === moduleListLength : false
                    }
                    moduleSpacing
                  />
                )
              })}
            </ConditionalWrapper>
          )
        })}
    </>
  )
}
